/**
 * @fileoverview Runtime configuration as a validated domain value.
 *
 * This exists because of a real failure. The SPA previously read its Cognito
 * pool and API base at module scope with hardcoded fallbacks to a specific AWS
 * account. When that account went away, the app still built, still deployed,
 * and still returned HTTP 200 — while pointing at infrastructure that no longer
 * existed. A missing environment variable silently became a broken deployment.
 *
 * So: no fallbacks. Configuration is parsed once at the composition root and
 * either yields a complete `AppConfig` or fails loudly with the exact names of
 * the variables that were missing. A build that isn't told where to point is a
 * bug, and it should announce itself at boot rather than at first sign-in.
 *
 * Pure by construction — no imports, no `import.meta`, no I/O — so it is
 * directly testable and carries no adapter concerns.
 */

/** Where this build points. Every field is required; there are no defaults. */
export interface AppConfig {
  /** Cognito user pool id, e.g. `us-east-2_NSboCuTJm`. */
  readonly cognitoUserPoolId: string;
  /** Cognito app client id for the SPA (public, PKCE — never a secret). */
  readonly cognitoClientId: string;
  /** AWS region hosting the pool, e.g. `us-east-2`. */
  readonly cognitoRegion: string;
  /** API origin, no trailing slash (normalized here so callers can concatenate). */
  readonly apiBaseUrl: string;
  /** Foundry-compatible deployment identity supplied to Control Panel hydration. */
  readonly controlPanelEnrollmentRid: string;
  /** Foundry-compatible organization identity supplied separately from enrollment. */
  readonly controlPanelOrganizationRid: string;
}

/** The env keys read, exported so tests and tooling can assert on them. */
export const CONFIG_KEYS = {
  cognitoUserPoolId: "VITE_COGNITO_USER_POOL_ID",
  cognitoClientId: "VITE_COGNITO_CLIENT_ID",
  cognitoRegion: "VITE_COGNITO_REGION",
  apiBaseUrl: "VITE_API_BASE_URL",
  controlPanelEnrollmentRid: "VITE_CONTROL_PANEL_ENROLLMENT_RID",
  controlPanelOrganizationRid: "VITE_CONTROL_PANEL_ORGANIZATION_RID",
} as const;

export class ConfigError extends Error {
  readonly missing: readonly string[];
  constructor(missing: readonly string[]) {
    super(
      `IronCase is not configured: missing ${missing.join(", ")}. ` +
        `These are baked in at build time, so set them in the build environment ` +
        `(see infra/live/dev/us-east-2/{cognito,app-runtime} terragrunt outputs) and rebuild.`,
    );
    this.name = "ConfigError";
    this.missing = missing;
  }
}

/** A record of environment values. `import.meta.env` satisfies this shape. */
export type ConfigSource = Readonly<Record<string, string | boolean | undefined>>;

/**
 * Parse and validate configuration.
 *
 * @throws ConfigError listing every missing key at once — reporting them one at
 * a time would mean one rebuild per variable to discover the full set.
 */
export function parseAppConfig(source: ConfigSource): AppConfig {
  const read = (key: string): string => {
    const raw = source[key];
    return typeof raw === "string" ? raw.trim() : "";
  };

  const missing = Object.values(CONFIG_KEYS).filter((key) => read(key) === "");
  if (missing.length > 0) throw new ConfigError(missing);

  return Object.freeze({
    cognitoUserPoolId: read(CONFIG_KEYS.cognitoUserPoolId),
    cognitoClientId: read(CONFIG_KEYS.cognitoClientId),
    cognitoRegion: read(CONFIG_KEYS.cognitoRegion),
    // Trailing slashes are stripped so `${apiBaseUrl}/health` can never produce
    // a double slash, which some gateways treat as a distinct (404) path.
    apiBaseUrl: read(CONFIG_KEYS.apiBaseUrl).replace(/\/+$/, ""),
    controlPanelEnrollmentRid: read(CONFIG_KEYS.controlPanelEnrollmentRid),
    controlPanelOrganizationRid: read(CONFIG_KEYS.controlPanelOrganizationRid),
  });
}
